import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Trash2, Ban, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const NutsMembersAdmin = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const authHeaders = React.useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/nuts/members`, { headers: authHeaders });
      setMembers(r.data);
    } catch (e) {
      toast.error('Error cargando miembros');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleBan = async (m) => {
    const newStatus = m.status === 'banned' ? 'approved' : 'banned';
    try {
      await axios.patch(
        `${API_URL}/api/nuts/members/${m.id}`,
        { status: newStatus },
        { headers: authHeaders }
      );
      toast.success(newStatus === 'banned' ? 'Miembro suspendido' : 'Miembro restaurado');
      load();
    } catch (e) {
      toast.error('Error actualizando estado');
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`¿Eliminar a "${m.nickname}" definitivamente?`)) return;
    try {
      await axios.delete(`${API_URL}/api/nuts/members/${m.id}`, { headers: authHeaders });
      toast.success('Miembro eliminado');
      load();
    } catch (e) {
      toast.error('Error eliminando miembro');
    }
  };

  if (loading) return <div className="py-10 text-center text-slate-500">Cargando miembros...</div>;

  return (
    <div className="space-y-4" data-testid="nuts-members-admin">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Miembros Nuts d'Andorra</h3>
          <p className="text-xs text-slate-500">{members.length} miembros registrados</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="nuts-refresh-btn">
          Refrescar
        </Button>
      </div>

      {members.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-slate-500">
            Aún no hay miembros registrados.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {members.map((m) => (
            <Card key={m.id} className={m.status === 'banned' ? 'opacity-60' : ''}>
              <CardContent className="py-4 flex items-center gap-4">
                {m.avatar_url && (
                  <img
                    src={m.avatar_url}
                    alt={m.nickname}
                    className="w-14 h-14 rounded-full border-2 border-emerald-700 bg-emerald-50 flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h4 className="text-base font-semibold truncate" data-testid={`nuts-member-${m.id}-nickname`}>{m.nickname}</h4>
                    {m.status === 'banned' && (
                      <span className="text-[10px] uppercase tracking-wider bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                        Banned
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{m.email}</p>
                  {(m.full_name || m.surnames || m.mobile) && (
                    <p className="text-xs text-slate-600 mt-1 truncate">
                      {[m.full_name, m.surnames].filter(Boolean).join(' ')}
                      {m.mobile && ` · ${m.mobile}`}
                    </p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant={m.status === 'banned' ? 'default' : 'outline'}
                    onClick={() => toggleBan(m)}
                    className={m.status === 'banned' ? 'bg-emerald-700 hover:bg-emerald-800' : ''}
                    data-testid={`nuts-toggle-ban-${m.id}`}
                  >
                    {m.status === 'banned' ? (
                      <><CheckCircle2 className="h-4 w-4 mr-1" /> Restaurar</>
                    ) : (
                      <><Ban className="h-4 w-4 mr-1" /> Banir</>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => remove(m)}
                    data-testid={`nuts-delete-${m.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default NutsMembersAdmin;
